import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzQueryInputWebComponent } from '../../../ui/gzQueryInput';
import { SearchQueryPanelEvents, SearchQueryProps } from '../../xdrSearchQueryPanel/types';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzCountDownWebComponent } from '../../../ui/gzCountDown';
import { GzCountDownEvents } from '../../../ui/gzCountDown';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { LiveSearchResultsGridComponent } from '../../../xdr/liveSearch/resultsGrid';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { LIVE_CODE_EDITOR_OPTIONS, LiveSearchFilters, LocalState } from '../../xdrSearchLive/types';
import { htmlDecode } from '../../../utils/escape.utils';
import { LiveSearchMetadataWebComponent } from '../../../xdr/liveSearch/metadata';
import { CompanySelectorWebComponent } from '../../../companySelector';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import _ from 'lodash';
import { FILTER_CHANGED, TARGET_FILTERS_TYPES } from '../types/constants';
import { API_TYPE_NIRVANA } from '../../../webcomponents.constants';
import { LabelWidthValues } from '../../../types/common/types';
import { GzEndpointFilterWebComponent } from '../../../ui/gzEndpointFilter';
import { EndpointListEvents } from '../../../ui/gzEndpointList/types';
import { TagsNetworkFilterWebComponent } from '../../../tagsManager/tagsNetworkFilter/index';
import { CodeEditorValue } from '../../../../../../libs/shared/code-editor/src/interface';
import { GzOperatingSystemFilterWebComponent } from '../../../ui/gzOperatingSystem';
import { OperatingSystemListEvents } from '../../../ui/gzOperatingSystem/types';
import { ButtonColor, ButtonType } from '../../../ui/gzIconButton/types';
import { AvailableCompaniesSource } from '../../../../../../libs/shared/dtos/types/dashboards/src/lib/shared-dtos-dashboards-gz-console';

export class XdrLiveSearchQueryPanelWebComponent extends LitElement {
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  pageTitle = '';

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  searchQueryProps: SearchQueryProps = {
    queryInputPlaceholder: '',
    queryBtnString: '',
    clearBtnString: '',
    errorMessages: {},
    hintTextString: '',
    helperHref: '',
    manualResize: false,
    enterKeyEnabled: false,
  };

  public translateFn: (string) => string;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  @query('gz-search-query')
  searchQuery: GzQueryInputWebComponent;

  @query('#execution-countdown')
  executionCountdown: GzCountDownWebComponent;

  @query('#countdown-finished-text')
  countdownFinishedText: GzTextWebComponent;

  @query('#countdown-label')
  countdownLabel: GzTextWebComponent;

  @query('#countdown-container')
  countdownContainer: HTMLElement;

  @query('gz-live-search-results-grid')
  grid: LiveSearchResultsGridComponent;

  @query('#liveSearchExportCsv')
  exportCsvButton: GzButtonWebComponent;

  @query('gz-live-search-metadata')
  liveSearchMetadata: LiveSearchMetadataWebComponent;

  @query('gz-company-selector')
  companySelector: CompanySelectorWebComponent;

  @query('gz-tags-network-filter')
  tagsNetworkFilter: TagsNetworkFilterWebComponent;

  @query('gz-endpoint-filter')
  endpointFilter: GzEndpointFilterWebComponent;

  @query('gz-operating-system-filter')
  operatingSystemFilter: GzOperatingSystemFilterWebComponent;

  // values in seconds - when reached the container will be assigned class milestone-{index}
  countdownMilestones = [600, 300];

  private inProgressCountdown: boolean;
  private targetCompanies: Target[];
  private dependencies: any;

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="header">
          <div class="smartviews-header">
            <gz-title class="title" fontweight="500" fontsize="20">${this.pageTitle}</gz-title>
            <div class="smartviews-actions">
              <gz-tooltip class="tooltip" tooltipText="${this.translateFn('Overwrite the current query.')}">
                <gz-button
                  id="liveSearchSave"
                  class="liveSearchButtons"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleSaveClick}"
                >${this.translateFn('Save#preserve state')}
                </gz-button>
              </gz-tooltip>
              <gz-tooltip class="tooltip" tooltipText="${this.translateFn('Save the current query as a new entry.')}">
                <gz-button
                  id="liveSearchSaveAs"
                  class="liveSearchButtons"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleSaveAsClick}"
                >${this.translateFn('Save as')}
                </gz-button>
              </gz-tooltip>
              <gz-tooltip class="tooltip" tooltipText="${this.translateFn('Revert the query to last saved state.')}">
                <gz-button
                  id="liveSearchDiscard"
                  class="liveSearchButtons"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleDiscardClick}"
                >${this.translateFn('Discard changes')}
                </gz-button>
              </gz-tooltip>
              <gz-tooltip class="tooltip" tooltipText="${this.translateFn('Reset filters.')}">
                <gz-button
                  id="liveSearchResetFilters"
                  class="liveSearchButtons"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleResetFiltersClick}"
                >${this.translateFn('Reset filters')}
                </gz-button>
              </gz-tooltip>
              <gz-tooltip class="tooltip" tooltipText="${this.translateFn('Download Results as CSV')}">
                <gz-icon-button
                  id="liveSearchExportCsv"
                  class="liveSearchButtons"
                  icon="ds-icon-export"
                  fontsize="20"
                  buttonType="${ButtonType.SQUARE}"
                  buttonColor="${ButtonColor.SECONDARY}"
                  @click="${this.handleExportCsvClick}"
                ></gz-icon-button>
              </gz-tooltip>
            </div>
          </div>
          <div class="smartviews-filters">
            <gz-company-selector id="companySelectorDropdown" class="company-selector"></gz-company-selector>
            <gz-operating-system-filter id="operatingSystemSelectorDropdown" labelWidth="${
              LabelWidthValues.LARGE
            }"></gz-operating-system-filter>
            <gz-tags-network-filter id="tags-network-filter" label="${this.translateFn('Tags')}" labelWidth="${
      LabelWidthValues.LARGE
    }" }"></gz-tags-network-filter>
            <gz-endpoint-filter id="endpoint-filter"></gz-endpoint-filter>
         </div>
        </div>
        <div>
          <gz-search-query
            .runQueryBtnString=${this.searchQueryProps.queryBtnString}
            .clearBtnString=${this.searchQueryProps.clearBtnString}
            .queryInputPlaceholder=${this.searchQueryProps.queryInputPlaceholder}
            .errorMessages=${this.searchQueryProps.errorMessages}
            .hintTextString=${this.searchQueryProps.hintTextString}
            manualResize=${this.searchQueryProps.manualResize}
            enterKeyEnabled=${this.searchQueryProps.enterKeyEnabled}
            class="px-2 py-1"
          >
          </gz-search-query>
        </div>
        <div id="countdown-container" class="hidden">
          <gz-text id="countdown-label"></gz-text>
          <gz-count-down id="execution-countdown"></gz-count-down>
        </div>
        <gz-text id="countdown-finished-text" class="hidden">${this.translateFn('Results have expired.')}</gz-text>
        <gz-live-search-results-grid></gz-live-search-results-grid>
        <gz-live-search-metadata></gz-live-search-metadata>
      </div>
      </div>
    `;
  }

  handleSaveClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.SAVE, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleSaveAsClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.SAVE_AS, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleDiscardClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.DISCARD, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleResetFiltersClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.RESET_FILTERS, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleExportCsvClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.EXPORT_CSV, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  public setTextboxCompact(): void {
    this.searchQuery.setInputRows(3, 10);
    this.searchQuery.debouncedExpandWindowByRowCount();
  }

  public setTextboxExpanded(): void {
    this.searchQuery.setInputRows(10, 10);
    this.searchQuery.debouncedExpandWindowByRowCount();
  }

  public createResultCountdownFromExecution(expireAt: number | undefined) {
    if (!this.initCountdownStart(expireAt)) {
      return;
    }
    this.inProgressCountdown = false;
    this.countdownLabel.innerText = this.translateFn('Deleting results in:');
    this.executionCountdown.startTimer();
  }

  public createInProgressCountdownFromExecution(expireAt: number | undefined) {
    if (!this.initCountdownStart(expireAt)) {
      return;
    }
    this.inProgressCountdown = true;
    this.countdownLabel.innerText = this.translateFn('Query in progress:');
    this.executionCountdown.startTimer();
  }

  public showCountdownText() {
    this.countdownContainer.classList.add(ElementClass.HIDDEN);
    this.countdownFinishedText.classList.add(ElementClass.VISIBLE);
  }

  public hideCountdown() {
    if (this.countdownContainer) {
      this.countdownContainer.classList.add(ElementClass.HIDDEN);
      this.countdownFinishedText.classList.add(ElementClass.HIDDEN);
    }
  }

  public unsetQueryExecution() {
    this.hideCountdown();
    this.grid.unsetQueryExecution();
    this.liveSearchMetadata.unsetQueryExecution();
  }

  private initCountdownStart(expireAt: number | undefined): boolean {
    if (!expireAt || Date.now() / 1000 > expireAt) {
      return false;
    }

    this.resetCountdownDisplay();
    this.executionCountdown.dateToTimestamp = Math.floor(expireAt);
    this.checkIfMilestoneAlreadyReached(this.executionCountdown.dateToTimestamp);
    return true;
  }

  private checkIfMilestoneAlreadyReached(countdownTimestamp: number) {
    const currentTimestamp = Date.now() / 1000;
    this.countdownMilestones.forEach((milestoneTimeInSec, index) => {
      if (countdownTimestamp - currentTimestamp < milestoneTimeInSec) {
        this.enableCountdownMilestone(index);
      }
    });
  }

  private enableCountdownMilestone(milestoneKey: number) {
    this.clearCountdownMilestones();
    this.countdownContainer.classList.add('milestone-' + milestoneKey);
  }

  private clearCountdownMilestones() {
    this.countdownMilestones.forEach((element, index) => {
      this.countdownContainer.classList.remove('milestone-' + index);
    });
  }

  private resetCountdownDisplay() {
    this.clearCountdownMilestones();
    this.countdownContainer.classList.remove(ElementClass.HIDDEN);
    this.countdownFinishedText.classList.add(ElementClass.HIDDEN);
  }

  timerStoppedEventListener() {
    this.countdownContainer.classList.add(ElementClass.HIDDEN);
    this.exportCsvButton.disabled = true;
    if (!this.inProgressCountdown) {
      this.countdownFinishedText.classList.remove(ElementClass.HIDDEN);
      this.liveSearchMetadata.controller.render();
    }
    this.clearCountdownMilestones();
    this.grid.renderExpiredResults();
  }

  milestoneReachedEventListener(evt: CustomEvent) {
    this.enableCountdownMilestone(evt.detail.milestoneKey);
  }

  resetManualResize() {
    this.searchQuery.resetManualResize();
  }

  public enableDisableButtonsForSelectedView(
    newInput: string = '',
    oldInput: string = '',
    currentStateOfCurrentItem: LocalState,
    initialStateOfCurrentItem: LocalState,
    hasResults: boolean = false,
    isReadOnly: boolean = false
  ) {
    this.shouldDisableAllHeaderButtons(true);
    const saveButton = this.shadowRoot?.querySelector('#liveSearchSave');
    const saveAsButton = this.shadowRoot?.querySelector('#liveSearchSaveAs');
    const discardButton = this.shadowRoot?.querySelector('#liveSearchDiscard');
    const resetFiltersButton = this.shadowRoot?.querySelector('#liveSearchResetFilters');
    this.shouldDisableButton(this.shadowRoot?.querySelector('#liveSearchExportResultMetadataCsv'), false);
    this.shouldDisableButton(
      resetFiltersButton,
      _.isEqual(initialStateOfCurrentItem['targetFilters'], currentStateOfCurrentItem['targetFilters'])
    );
    this.shouldDisableButton(
      saveButton,
      isEmpty(newInput) ||
        isReadOnly ||
        (oldInput === newInput && _.isEqual(initialStateOfCurrentItem, currentStateOfCurrentItem))
    );
    this.shouldDisableButton(
      discardButton,
      oldInput === newInput && _.isEqual(currentStateOfCurrentItem, initialStateOfCurrentItem)
    );
    this.shouldDisableButton(
      saveAsButton,

      isEmpty(newInput)
    );
    this.shouldDisableExportButton(!hasResults);
  }

  public removeModifyMarkerFromTitle(): void {
    if (!this.pageTitle) {
      return;
    }
    const lastChar = this.pageTitle.slice(-1);
    if ('*' === lastChar) {
      this.pageTitle = this.pageTitle.slice(0, -1);
    }
  }

  public addModifyMarkerToTitle(): void {
    if (!this.pageTitle) {
      return;
    }
    const lastChar = this.pageTitle.slice(-1);
    if ('*' !== lastChar) {
      this.pageTitle += '*';
    }
  }

  public setQueryValue(newInput: string) {
    this.searchQuery.setValue(newInput);
  }

  public async getQueryValue(): Promise<CodeEditorValue> {
    return await this.searchQuery.getValue();
  }

  public shouldDisableExportButton(condition: boolean = false) {
    const exportCsvButton = this.shadowRoot?.querySelector('#liveSearchExportCsv');
    this.shouldDisableButton(exportCsvButton, condition);
  }

  public shouldDisableRunButton(condition: boolean = false) {
    this.shouldDisableButton(this.searchQuery.runQueryButton, condition);
  }

  public shouldDisableClearButton(condition: boolean = false) {
    this.shouldDisableButton(this.searchQuery.clearButton, condition);
  }

  public shouldDisableAllHeaderButtons(disable: boolean) {
    const buttons = this.shadowRoot?.querySelectorAll('.liveSearchButtons');

    if (disable) {
      buttons?.forEach(button => {
        button?.setAttribute('disabled', 'true');
      });
    } else {
      buttons?.forEach(button => {
        button?.removeAttribute('disabled');
      });
    }
  }

  private shouldDisableButton(button, disable: boolean) {
    if (disable) {
      button?.setAttribute('disabled', 'true');
    } else {
      button?.removeAttribute('disabled');
    }
  }

  private async onUpdateComplete(event: WebComponentEvent) {
    const codeEditorOptions = LIVE_CODE_EDITOR_OPTIONS;

    this.searchQuery.setupCodeEditor('sqlite', codeEditorOptions.languageOptions, {
      ...codeEditorOptions.editorOptions,
      theme: 'sqliteTheme',
    });

    this.setNewQueryPanel();
    this.shouldDisableAllHeaderButtons(true);
  }

  private setNewQueryPanel() {
    this.setQueryValue('');
    this.pageTitle = htmlDecode(this.translateFn('New query'));
    this.liveSearchMetadata.controller.setPageTitle(this.pageTitle);
  }

  public injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
    this.grid.injectDependencies(dependencies);
    this.grid.start().then().catch();
    this.executionCountdown.addEventListener(GzCountDownEvents.TIMER_HAS_STOPPED, () => {
      this.timerStoppedEventListener();
    });

    this.liveSearchMetadata.injectDependencies(dependencies);
    this.liveSearchMetadata.start().then().catch();
    this.tagsNetworkFilter.injectDependencies({
      ...dependencies,
    });
    this.tagsNetworkFilter.start().then().catch();
    this.endpointFilter.injectDependencies({
      ...dependencies,
    });

    this.endpointFilter.addEventListener('change', async function (e: Event): Promise<void> {
      e.stopPropagation();
    });

    this.endpointFilter.addEventListener(
      EndpointListEvents.ENDPOINT_SELECTION_CHANGED,
      async (e: WebComponentEvent): Promise<void> => {
        await this.dispatchChangeEvent();
        this.dispatchEvent(
          new CustomEvent(FILTER_CHANGED, {
            detail: {
              endpoints: e.detail.selectedFilterOptions,
              target: TARGET_FILTERS_TYPES.ENDPOINT,
            },
            bubbles: true,
          })
        );
      }
    );

    this.tagsNetworkFilter.addEventListener('change', async (e: WebComponentEvent): Promise<void> => {
      this.dispatchEvent(
        new CustomEvent(FILTER_CHANGED, {
          detail: {
            tags: e.detail.selectedValues,
            target: TARGET_FILTERS_TYPES.TAG,
          },
          bubbles: true,
        })
      );
    });

    this.executionCountdown.addEventListener(GzCountDownEvents.TIMER_MILESTONE_REACHED, (evt: CustomEvent) => {
      this.milestoneReachedEventListener(evt);
    });

    this.searchQuery.addEventListener(SearchQueryPanelEvents.UPDATE_COMPLETE, async (evt: WebComponentEvent) => {
      await this.onUpdateComplete(evt);
    });

    this.buildOperatingSystemSelectorDropdown();
    this.executionCountdown.milestones = this.countdownMilestones;
  }

  public setOptionsForOperatingSystemsFilter(operatingSystems: (string | number)[]): void {
    this.operatingSystemFilter.controller.setFilterOptions(operatingSystems);
  }

  public buildOperatingSystemSelectorDropdown(): void {
    this.operatingSystemFilter.label = this.translateFn('OS');
    this.operatingSystemFilter.confirmActionButtonString = this.translateFn('Apply');
    this.operatingSystemFilter.listTitle = this.translateFn('All');
    this.operatingSystemFilter.canSelectAll = true;
    this.operatingSystemFilter.allItemsLabel = this.translateFn('All');
    this.operatingSystemFilter.injectDependencies(this.dependencies);
    this.operatingSystemFilter.addEventListener(
      OperatingSystemListEvents.OPERATING_SYSTEM_SELECTION_CHANGED,
      async (e: WebComponentEvent): Promise<void> => {
        await this.dispatchChangeEvent();
        this.dispatchEvent(
          new CustomEvent(FILTER_CHANGED, {
            detail: {
              operatingSystems: e.detail.selectedFilterOptions,
              target: TARGET_FILTERS_TYPES.OPERATING_SYSTEM,
            },
            bubbles: true,
          })
        );
      }
    );
  }

  async buildCompanySelectorDropdown(companyId: string, companyName: string) {
    companyName = companyName ?? this.translateFn('Company not found');
    this.companySelector.setSelectedOption({
      title: companyName,
      value: companyId,
    });
    this.companySelector.companySelectorParams = {
      options: {
        source: AvailableCompaniesSource.FROM_LIVESEARCH,
        countRecords: false
      }
    };
    if (!this.targetCompanies) {
      this.targetCompanies = await this.dependencies.api.clients.Company.getAvailableCompanies(
        this.companySelector.companySelectorParams,
        getRequestDefaultOptions()
      );
    }
    this.companySelector.selectedTarget = {
      companyId: companyId,
      companyName: companyName,
    };
    this.companySelector.label = this.translateFn('Company');
    this.companySelector.placeholder = this.translateFn('Select company');
    this.companySelector.availableCompanies = this.targetCompanies;
    this.companySelector.onCompanyClicked = this.onCompanyClicked.bind(this);
    this.companySelector.onCompanyChangedWithSuccess = this.onCompanyChangedWithSuccess.bind(this);
    const nirvanaDep = _.cloneDeep(this.dependencies);
    nirvanaDep.api.type = API_TYPE_NIRVANA;
    this.companySelector.injectDependencies(nirvanaDep);
    this.companySelector.start().catch(console.error);
  }

  /**
   * sets the selected options for the filters
   */
  public async setSelectedFilterOptions(filters: LiveSearchFilters): Promise<void> {
    this.companySelector.selectedTarget = {
      companyId: filters.companyId,
      companyName: filters.companyName,
    };
    this.setSelectedEndpoints(filters.companyId, filters.endpoints);
    await this.setSelectedTags(filters.companyId, filters.tags);
  }

  /**
   * sets the company programatically
   */
  public async setSelectedCompany(company: Target) {
    this.companySelector.selectedTarget = {
      companyId: company.companyId,
      companyName: company.companyName,
    };
    this.companySelector.setSelectedOption({
      title: company.companyName,
      value: company.companyId,
    });
    await this.setFilterOptionsOfCompany(company.companyId);
    this.dispatchEvent(
      new CustomEvent(FILTER_CHANGED, {
        detail: {
          companyId: company.companyId,
          companyName: company.companyName,
          target: TARGET_FILTERS_TYPES.COMPANY,
        },
        bubbles: true,
      })
    );
  }

  async onCompanyClicked(clickedCompany: Target) {
    await this.setSelectedCompany(clickedCompany);
    if (this.dependencies.getUser().mdrSuperAnalyst) {
      this.dependencies.setActiveCompany(clickedCompany.companyId, clickedCompany.companyName);
    }
  }

  private async onCompanyChangedWithSuccess() {
    await this.dispatchChangeEvent();
  }

  private async dispatchChangeEvent() {
    const input = await this.searchQuery.getValue();
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          input: input.raw,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * update the options of the endpoint filter dropdown to take into account filtering by company
   */
  public async updateEndpointFilterProps(companyId: string, selectedValues = []): Promise<any> {
    this.endpointFilter.label = this.translateFn('Endpoint name');
    this.endpointFilter.labelWidth = LabelWidthValues.LARGE;
    this.endpointFilter.noSearchResultsString = this.translateFn('No results found');
    this.endpointFilter.confirmActionButtonString = this.translateFn('Apply');
    this.endpointFilter.searchPlaceholderString = this.translateFn('Search');
    this.endpointFilter.canSelectAll = true;
    this.endpointFilter.allItemsLabel = this.translateFn('All');
    this.endpointFilter.errorMessage = this.translateFn('Error');
    this.endpointFilter.overwriteList = true;
    this.endpointFilter.companyId = companyId;

    await this.endpointFilter.controller.setFilterOptions(selectedValues);
  }

  private async setFilterOptionsOfCompany(companyId: string): Promise<any> {
    this.endpointFilter.setCompanyId(companyId);
    this.tagsNetworkFilter.companyIds = [companyId];
    await this.tagsNetworkFilter.setSelectedItems([]);
    await this.endpointFilter.controller.setFilterOptions([]);
    this.dispatchEvent(
      new CustomEvent(FILTER_CHANGED, {
        detail: {
          endpoints: this.endpointFilter.controller.getSpecialSelectedOption(),
          target: TARGET_FILTERS_TYPES.ENDPOINT,
        },
        bubbles: true,
      })
    );
  }

  public setSelectedEndpoints(companyId: string, selectedValues: Array<string | number> = []): void {
    this.endpointFilter.setCompanyId(companyId);
    this.endpointFilter.selectedFilterOptions = selectedValues.map(endpointId => {
      return {
        label: '',
        value: endpointId,
      };
    });
  }

  public getSelectedEndpoints(): Array<string | number> {
    const selectedElements = this.endpointFilter.selectedFilterOptions;
    if (!selectedElements) {
      return [];
    }

    return selectedElements.map(endpoint => {
      return endpoint.value;
    });
  }

  public async setSelectedTags(companyId: string, selectedValues: Array<string> = []): Promise<void> {
    this.tagsNetworkFilter.companyIds = [companyId];
    await this.tagsNetworkFilter.setSelectedItems(selectedValues);
  }

  public getSelectedTags(): Array<string> {
    return this.tagsNetworkFilter.selectedItems;
  }

  public getSpecialSelectedOptionEndpointFilter(): (number | string)[] | undefined {
    return this.endpointFilter.controller.getSpecialSelectedOption().map(endpoint => {
      return endpoint.value;
    });
  }

  public getAllOptionsValuesOperatingSystems(): (string | number)[] {
    return this.operatingSystemFilter.controller.getAllOptionsValues();
  }
}
